import { defineStore } from 'pinia'
import { useAuthStore } from './auth'
import { ref, reactive, computed } from 'vue'
import { api } from '@/libs/mande'
import { type ErrorModel, isErrorModel, type ErrorResponse, type ErrorDetail } from '@/models/error'

export interface User {
  id: number
  nickname: string
  full_name: string
  phone: string
  email: string
  birthdate: string
  role: string
  gender_identity: string
  notes: string
  emergency_contact_name: string
  emergency_contact_phone: string
  profile_pic: string
  created_at: string
  updated_at: string
  sudo: boolean
  paid: boolean
}

export interface EditForm {
  email: string
  emergency_contact_name: string
  emergency_contact_phone: string
  full_name: string
  gender_identity: string
  nickname: string
}

export interface ClinicianListUsersResponse {
  users: User[]
}

export interface UsersResponse {
  users: User[]
}

export interface CreateUserResponse {
  id: number
  role: string
}

export const useUserStore = defineStore('user', () => {
  const current_user = reactive<User>({} as User)
  const error = ref<ErrorModel | null>(null)

  const profile_pic = computed(() => current_user.profile_pic || '')

  const setCurrentUser = (user: User | null): void => {
    if (user === null) {
      Object.keys(current_user).forEach((key) => {
        // current_user[key as keyof User] = null!
        delete current_user[key as keyof User] //return current user to empty object when user is null
      })
      return
    }
    Object.assign(current_user, user)
    ;(window as any).currentUserId = current_user.id
    ;(window as any).currentUserEmail = current_user.email
  }

  const createUser = async (user: User): Promise<User | ErrorModel> => {
    const resp = await api.post('/users', user)
    if (isErrorModel(resp)) return resp as ErrorModel
    user.id = (resp as CreateUserResponse).id
    user.role = (resp as CreateUserResponse).role
    setCurrentUser(user)
    return user
  }

  const saveNotes = async (notes: string): Promise<void | ErrorModel> => {
    try {
      const resp = await api.put('/notes', {
        notes: notes
      })
      return resp as ErrorModel
    } catch (err: ErrorResponse | any) {
      console.error(err)
      if (isErrorModel(err.body)) {
        console.error('setting error', err.body)
        error.value = err.body as ErrorModel
        return error.value
      }
      error.value = {
        detail: 'An unknown error occurred',
        errors: [] as ErrorDetail[]
      } as ErrorModel
      return error.value
    }
  }

  const updateUser = async (user: User): Promise<User | ErrorModel> => {
    try {
      const resp = await api.put('/user', {
        nickname: user.nickname,
        full_name: user.full_name,
        email: user.email,
        notes: user.notes,
        gender_identity: user.gender_identity,
        emergency_contact_name: user.emergency_contact_name,
        emergency_contact_phone: user.emergency_contact_phone
      })
      Object.assign(current_user, resp as User)
      error.value = null
      return resp as User
    } catch (err: ErrorResponse | any) {
      console.error(err)
      if (isErrorModel(err.body)) {
        console.error('setting error', err.body)
        error.value = err.body as ErrorModel
        return error.value
      }
      error.value = {
        detail: 'An unknown error occurred',
        errors: [] as ErrorDetail[]
      } as ErrorModel
      return error.value
    }
  }

  const editUser = async (editForm: EditForm): Promise<User | ErrorModel> => {
    try {
      const resp = await api.patch('/user', {
        nickname: editForm.nickname,
        full_name: editForm.full_name,
        email: editForm.email,
        gender_identity: editForm.gender_identity,
        emergency_contact_name: editForm.emergency_contact_name,
        emergency_contact_phone: editForm.emergency_contact_phone
      })
      Object.assign(current_user, resp as User)
      error.value = null
      return resp as User
    } catch (err: ErrorResponse | any) {
      console.error(err)
      if (isErrorModel(err.body)) {
        console.error('setting error', err.body)
        error.value = err.body as ErrorModel
        return error.value
      }
      error.value = {
        detail: 'An unknown error occurred',
        errors: [] as ErrorDetail[]
      } as ErrorModel
      return error.value
    }
  }

  const uploadProfilePicture = async (profile_picture: string): Promise<void | ErrorModel> => {
    // debugger
    await api.patch('/upload_avatar', { profile_pic: profile_picture })
    await forceGetCurrentUser()
  }

  const forceGetCurrentUser = async (): Promise<User | null> => {
    current_user.id = 0 // force a refresh
    return getCurrentUser()
  }

  const getCurrentUser = async (): Promise<User | null> => {
    const authStore = useAuthStore()
    if ('id' in current_user && current_user.id !== 0) return current_user
    if (authStore.session !== null) {
      const resp = await api.get('/user')
      if (resp !== undefined && resp !== null) {
        resp instanceof Error ? console.error(resp) : setCurrentUser(resp as User)
      }

      return current_user
    }
    return null
  }

  const adminGetUsers = async (): Promise<User[] | ErrorModel> => {
    const resp = await api.get('/admin/users')
    if (isErrorModel(resp)) return resp as ErrorModel
    return (resp as UsersResponse).users
  }

  const adminGetUser = async (id: number): Promise<User | ErrorModel> => {
    const resp = await api.get('/admin/users/' + id)
    if (isErrorModel(resp)) return resp as ErrorModel
    return resp as User
  }

  const adminUpdateUser = async (user: User): Promise<null | ErrorModel> => {
    return api.put('/admin/users/' + user.id, { role: user.role })
  }

  const adminDeleteUser = async (user: User): Promise<null | ErrorModel> => {
    return api.delete('/admin/users/' + user.id)
  }

  const IsAdmin = async (): Promise<boolean> => {
    const u = await getCurrentUser()
    return u !== null && u.role === 'admin'
  }

  const clinicianGetUser = async (id: number): Promise<User | ErrorModel> => {
    const resp = await api.get('/clinician/users/' + id)
    if (isErrorModel(resp)) return resp as ErrorModel
    return resp as User
  }

  const clinicianListUsers = async (): Promise<ClinicianListUsersResponse | ErrorModel> => {
    const resp = await api.get('/clinician/users')
    if (isErrorModel(resp)) return resp as ErrorModel
    return resp as ClinicianListUsersResponse
  }

  const IsUser = async (): Promise<boolean> => {
    const u = await getCurrentUser()
    return u !== null && u.role === 'user'
  }

  const IsClinician = async (): Promise<boolean> => {
    const u = await getCurrentUser()
    return u !== null && u.role === 'clinician'
  }

  return {
    error,
    current_user,
    profile_pic,
    createUser,
    updateUser,
    editUser,
    saveNotes,
    uploadProfilePicture,
    setCurrentUser,
    getCurrentUser,
    forceGetCurrentUser,
    adminGetUsers,
    adminGetUser,
    adminUpdateUser,
    adminDeleteUser,
    IsAdmin,
    clinicianGetUser,
    clinicianListUsers,
    IsUser,
    IsClinician
  }
})
